/**
 * Records an uncaught render error as an OTel exception span, then renders
 * nothing further for that subtree — the app already relies on this shell
 * boundary being the outermost error catcher, so a nested crash still leaves
 * the surrounding chrome intact rather than blanking the whole page.
 *
 * Only `error.message` is recorded, and it still passes through
 * `PrivacySanitizingSpanProcessor` before export. `error.stack` is never
 * forwarded: stack traces can include local file paths and, for this app's
 * validation errors, fragments of user-entered programme/module content in
 * the message.
 *
 * @module components/shell/TelemetryBoundary
 */
import React from "react";

import { trace } from "@opentelemetry/api";

export interface TelemetryBoundaryProps {
  children: React.ReactNode;
}

interface TelemetryBoundaryState {
  hasError: boolean;
}

export class TelemetryBoundary extends React.Component<
  TelemetryBoundaryProps,
  TelemetryBoundaryState
> {
  constructor(props: TelemetryBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): TelemetryBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error): void {
    const span = trace.getTracer("react-error-boundary").startSpan("react.render_error");
    span.recordException({ message: error.message });
    span.end();
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;
    return hasError ? null : children;
  }
}
